import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  UpdateSupplier, ViewSupplier
} from "../../../redux/services/SupplierService/supplier.Services";
import {
  successToast,
  errorToast,
} from "../../../Utils/toast";
import {
  closeProductModal,
  refreshProductList,
} from "../../../redux/features/productModalSlice";

import { StateList, CityList } from "../../../redux/services/ProductService/product.Services";

function EditCategory({ data, onSuccess }) {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    supplier_name: "",
    company_name: "",
    mobile_number: "",
    email: "",
    gst_number: "",
    address: "",
    city_id: "",
    state_id: "",
    state_name: "",
    city_name: "",
    pincode: "",
    status: true,
  });

  // Fetch states on component mount
  useEffect(() => {
    fetchStates();
  }, []);

  // Set form data when data and states are loaded
  useEffect(() => {
    if (data && states.length > 0) {
      const stateObj = states.find(
        (item) => item.name === data.state
      );

      const cityObj = cities.find(
        (item) => item.name === data.city
      );

      setFormData({
        supplier_name: data.supplier_name || "",
        company_name: data.company_name || "",
        mobile_number: data.mobile_number || "",
        email: data.email || "",
        gst_number: data.gst_number || "",
        address: data.address || "",
        city_id: cityObj ? cityObj.id : "",
        state_id: stateObj ? stateObj.id : "",
        state_name: data.state || "",
        city_name: data.city || "",
        pincode: data.pincode || "",
        status: data.status === 1,
      });

      if (stateObj) {
        fetchCities(stateObj.id);
      }
    }
  }, [data, states]);

  useEffect(() => {
    if (formData.state_id && data?.city && cities.length > 0) {
      const cityObj = cities.find(
        (item) => item.name === data.city
      );
      if (cityObj) {
        setFormData((prev) => ({
          ...prev,
          city_id: cityObj.id,
          city_name: cityObj.name,
        }));
      }
    }
  }, [cities, formData.state_id, data]);

  const fetchStates = async () => {
    try {
      setIsLoadingStates(true);
      const response = await StateList();
      setStates(response?.data || []);
    } catch (error) {
      console.error("Error fetching states:", error);
      errorToast("Failed to load states");
    } finally {
      setIsLoadingStates(false);
    }
  };

  const fetchCities = async (stateId) => {
    try {
      setIsLoadingCities(true);
      const response = await CityList({
        state_id: stateId,
      });
      setCities(response.data || []);
    } catch (error) {
      console.log(error);
      errorToast("Failed to load cities");
    } finally {
      setIsLoadingCities(false);
    }
  };

  // Validation functions - Same as CreateSupplier
  const validate = () => {
    let newErrors = {};

    if (!formData.supplier_name) {
      newErrors.supplier_name = "Supplier name is required";
    }

    if (!formData.company_name) {
      newErrors.company_name = "Company is required";
    }

    if (!formData.mobile_number) {
      newErrors.mobile_number = "Mobile is required";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    }

    if (!formData.gst_number) {
      newErrors.gst_number = "GST Number is required";
    }

    if (!formData.address) {
      newErrors.address = "Address is required";
    }

    if (!formData.city_id) {
      newErrors.city = "City is required";
    }

    if (!formData.state_id) {
      newErrors.state = "State is required";
    }

    if (!formData.pincode) {
      newErrors.pincode = "Pin Code is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "state_id") {
      const selectedState = states.find((state) => state.id === parseInt(value));

      setFormData((prev) => ({
        ...prev,
        state_id: value,
        state_name: selectedState ? selectedState.name : "",
        city_id: "",
        city_name: "",
      }));

      setErrors((prev) => ({
        ...prev,
        state: "",
        city: "",
      }));

      if (value) {
        fetchCities(parseInt(value));
      } else {
        setCities([]);
      }
      return;
    }

    if (name === "city_id") {
      const selectedCity = cities.find((city) => city.id === parseInt(value));
      setFormData((prev) => ({
        ...prev,
        city_id: value,
        city_name: selectedCity ? selectedCity.name : "",
      }));

      setErrors((prev) => ({
        ...prev,
        city: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: name === "status" ? value === "Active" : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Use the same validate function as CreateSupplier
    if (!validate()) return;
    
    setIsLoading(true);

    try {
      const payload = {
        id: data?.id,
        supplier_name: formData.supplier_name,
        company_name: formData.company_name,
        mobile_number: formData.mobile_number,
        email: formData.email,
        gst_number: formData.gst_number,
        address: formData.address,
        city: formData.city_name,
        state: formData.state_name,
        pincode: formData.pincode,
        status: formData.status ? 1 : 0,
      };

      const res = await UpdateSupplier(payload);
      
      if (res.success) {
        successToast("Success", res.message || "Supplier updated successfully");
        
        dispatch(refreshProductList());
        dispatch(closeProductModal());

        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      if (err?.response?.status === 422) {
        setErrors(err.response.data.errors);
      } else {
        errorToast("Error", err?.response?.data?.message || "Something went wrong");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-form-wrapper">
      <form onSubmit={handleSubmit} className="row">
        <div className="col-md-6">
          <label>Supplier Name <span className="text-danger">*</span></label>
          <input
            type="text"
            name="supplier_name"
            value={formData.supplier_name}
            onChange={handleChange}
            className={`form-control ${errors.supplier_name ? 'is-invalid' : ''}`}
            placeholder="Enter supplier name"
          />
          {errors.supplier_name && (
            <div className="invalid-feedback">
              {Array.isArray(errors.supplier_name)
                ? errors.supplier_name[0]
                : errors.supplier_name}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label>Company Name <span className="text-danger">*</span></label>
          <input
            type="text"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            className={`form-control ${errors.company_name ? 'is-invalid' : ''}`}
            placeholder="Enter company name"
          />
          {errors.company_name && (
            <div className="invalid-feedback">
              {Array.isArray(errors.company_name)
                ? errors.company_name[0]
                : errors.company_name}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label>Mobile Number <span className="text-danger">*</span></label>
          <input
            type="text"
            name="mobile_number"
            value={formData.mobile_number}
            onChange={(e) => {
              handleChange({
                target: {
                  name: "mobile_number",
                  value: e.target.value.replace(/\D/g, "")
                }
              });
            }}
            maxLength={10}
            className={`form-control ${errors.mobile_number ? 'is-invalid' : ''}`}
            placeholder="Enter mobile number"
          />
          {errors.mobile_number && (
            <div className="invalid-feedback">
              {Array.isArray(errors.mobile_number)
                ? errors.mobile_number[0]
                : errors.mobile_number}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label>Email <span className="text-danger">*</span></label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
            placeholder="Enter email address"
          />
          {errors.email && (
            <div className="invalid-feedback">
              {Array.isArray(errors.email)
                ? errors.email[0]
                : errors.email}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label>GST Number <span className="text-danger">*</span></label>
          <input
            type="text"
            name="gst_number"
            value={formData.gst_number}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                gst_number: e.target.value.toUpperCase(),
              }));
              setErrors((prev) => ({
                ...prev,
                gst_number: "",
              }));
            }}
            style={{ textTransform: "uppercase" }}
            className={`form-control ${errors.gst_number ? 'is-invalid' : ''}`}
            placeholder="Enter GST number"
          />
          {errors.gst_number && (
            <div className="invalid-feedback">
              {Array.isArray(errors.gst_number)
                ? errors.gst_number[0]
                : errors.gst_number}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label>State <span className="text-danger">*</span></label>
          <select
            name="state_id"
            value={formData.state_id}
            onChange={handleChange}
            className={`form-control ${errors.state ? 'is-invalid' : ''}`}
            disabled={isLoadingStates}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>
          {errors.state && (
            <div className="invalid-feedback">
              {Array.isArray(errors.state)
                ? errors.state[0]
                : errors.state}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label>City <span className="text-danger">*</span></label>
          <select
            name="city_id"
            value={formData.city_id}
            onChange={handleChange}
            className={`form-control ${errors.city ? 'is-invalid' : ''}`}
            disabled={!formData.state_id || isLoadingCities}
          >
            <option value="">Select City</option>
            {cities.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))}
          </select>
          {errors.city && (
            <div className="invalid-feedback">
              {Array.isArray(errors.city)
                ? errors.city[0]
                : errors.city}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label>Pincode <span className="text-danger">*</span></label>
          <input
            type="text"
            name="pincode"
            value={formData.pincode}
            onChange={handleChange}
            maxLength={6}
            className={`form-control ${errors.pincode ? 'is-invalid' : ''}`}
            placeholder="Enter pincode"
          />
          {errors.pincode && (
            <div className="invalid-feedback">
              {Array.isArray(errors.pincode)
                ? errors.pincode[0]
                : errors.pincode}
            </div>
          )}
        </div>

        <div className="col-12">
          <label>Address <span className="text-danger">*</span></label>
          <textarea
            rows="3"
            name="address"
            placeholder="Enter Address"
            value={formData.address}
            onChange={handleChange}
            className={`form-control ${errors.address ? 'is-invalid' : ''}`}
          />
          {errors.address && (
            <div className="invalid-feedback">
              {Array.isArray(errors.address)
                ? errors.address[0]
                : errors.address}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label>Status</label>
          <select
            name="status"
            value={formData.status ? "Active" : "Inactive"}
            onChange={handleChange}
            className="form-control"
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="col-md-12 text-end mt-3">
          <button
            type="submit"
            disabled={isLoading}
            className="add-btn btn btn-primary w-100"
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Updating...
              </>
            ) : (
              "Update Supplier"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditCategory;